import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, post, put, del, getPaginated } from '@/lib/api'

import type { WorkflowStage, TrackType, TrackStatus } from '@company/shared'


// ==================== Types ====================



export type DevelopmentConfig = 'none' | 'frontend_only' | 'backend_only' | 'both'

export interface WorkflowConfig {
  id: string
  issueId: string
  productDiscussionEnabled: boolean
  uiDesignEnabled: boolean
  opsDeploymentEnabled: boolean
  developmentConfig: DevelopmentConfig
  currentStage: WorkflowStage
  currentStageStartedAt: string | null // 当前阶段开始时间
  discussionParticipants: string[] | null
  // 前端轨道
  frontendTrackStatus: TrackStatus
  frontendAssigneeId: string | null
  frontendAssigneeName: string | null
  frontendStartedAt: string | null // 前端轨道开始时间
  frontendSubmittedAt: string | null
  frontendApprovedAt: string | null
  frontendRejectReason: string | null
  // 后端轨道
  backendTrackStatus: TrackStatus
  backendAssigneeId: string | null
  backendAssigneeName: string | null
  backendStartedAt: string | null // 后端轨道开始时间
  backendSubmittedAt: string | null
  backendApprovedAt: string | null
  backendRejectReason: string | null
  // 阶段审核人
  stageReviewers: StageReviewers
  // 旧字段（兼容）
  frontendCompleted: boolean
  backendCompleted: boolean
  createdAt: string
  updatedAt: string | null
}

/**
 * 审核人信息
 */
export interface ReviewerInfo {
  id: string
  name: string | null
  isTeamLeader: boolean
}

/**
 * 阶段审核人配置
 */
export interface StageReviewers {
  productDiscussion: ReviewerInfo | null
  uiDesign: ReviewerInfo | null
  uiConfirmation: ReviewerInfo | null
  codeReview: ReviewerInfo | null
  opsDeployment: ReviewerInfo | null
  testing: ReviewerInfo | null
  productAcceptance: ReviewerInfo | null
}

/**
 * 设置阶段审核人输入
 */
export interface SetStageReviewerInput {
  stage: WorkflowStage
  reviewerId: string | null
}

/**
 * 添加阶段参与人员输入
 */
export interface AddStageParticipantsInput {
  employeeIds: string[]
}

export interface StageParticipant {
  id: string
  stage: string
  employeeId: string
  employeeName: string | null
  assignedBy: string
  assignedAt: string
  completedAt: string | null
}

export interface WorkflowHistoryRecord {
  id: string
  action: string
  fromStage: string | null
  toStage: string | null
  userId: string
  actorName: string | null
  trackType: string | null
  trackAssigneeId: string | null
  trackAssigneeName: string | null
  reason: string | null
  metadata: string | null
  createdAt: string
}

export interface MyWorkflowTask {
  issueId: string
  issueTitle: string
  projectId: string
  projectName: string
  currentStage: string
  role: 'owner' | 'participant'
  waitingTime: number
  requiresAction: boolean
  assignedAt: string
}

export interface InitializeWorkflowInput {
  productDiscussionEnabled?: boolean
  uiDesignEnabled?: boolean
  opsDeploymentEnabled?: boolean
  developmentConfig?: DevelopmentConfig
  discussionParticipants?: string[]
}

export interface AssignParticipantInput {
  stage: WorkflowStage
  employeeId: string
}

export interface RejectWorkflowInput {
  targetStage: WorkflowStage
  reason: string
}

export interface MyWorkflowTasksParams {
  role?: 'owner' | 'participant' | 'all'
  stage?: WorkflowStage
  page?: number
  pageSize?: number
}

// ==================== Track Types ====================

export interface MyTrackTask {
  issueId: string
  issueTitle: string
  projectId: string
  projectName: string
  track: TrackType
  trackStatus: TrackStatus
  role: 'owner' | 'assignee'
  assigneeId: string | null
  requiresAction: boolean
  waitingTime: number
}

export interface MyTrackTasksParams {
  track?: TrackType
  status?: TrackStatus
  role?: 'owner' | 'assignee' | 'all'
  page?: number
  pageSize?: number
}

export interface StartTrackInput {
  assigneeId: string
}

export interface RejectTrackInput {
  reason: string
}

export interface RollbackFromTestingInput {
  tracks: TrackType[]
  reason: string
}

// ==================== Query Keys ====================

export const workflowKeys = {
  all: ['workflow'] as const,
  config: (issueId: string) => [...workflowKeys.all, 'config', issueId] as const,
  participants: (issueId: string, stage?: WorkflowStage) =>
    [...workflowKeys.all, 'participants', issueId, stage] as const,
  history: (issueId: string) => [...workflowKeys.all, 'history', issueId] as const,
  myTasks: (params: MyWorkflowTasksParams) => [...workflowKeys.all, 'my-tasks', params] as const,
  myTrackTasks: (params: MyTrackTasksParams) => [...workflowKeys.all, 'my-track-tasks', params] as const,
}

// ==================== Stage Display Names ====================

export const STAGE_DISPLAY_NAMES: Record<WorkflowStage, string> = {
  created: '已创建',
  product_discussion: '产品研讨',
  ui_design: 'UI设计',
  ui_confirmation: 'UI确认',
  frontend_development: '前端开发',
  backend_development: '后端开发',
  parallel_development: '并行开发',
  code_review: '代码审查',
  ops_deployment: '运维部署',
  testing: '测试',
  product_acceptance: '产品验收',
  done: '已完成',
}

export function getStageDisplayName(stage: string): string {
  return STAGE_DISPLAY_NAMES[stage as WorkflowStage] ?? stage
}

// ==================== Hooks ====================

/**
 * 获取任务工作流配置
 */
export function useIssueWorkflow(issueId: string) {
  return useQuery({
    queryKey: workflowKeys.config(issueId),
    queryFn: () => get<WorkflowConfig | null>(`/pm/issues/${issueId}/workflow`),
    enabled: !!issueId,
  })
}

/**
 * 初始化任务工作流
 */
export function useInitializeWorkflow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, data }: { issueId: string; data: InitializeWorkflowInput }) =>
      post<WorkflowConfig>(`/pm/issues/${issueId}/workflow`, data),
    onSuccess: (_, { issueId }) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.config(issueId) })
    },
  })
}

/**
 * 分配阶段参与人员
 */
export function useAssignParticipant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, data }: { issueId: string; data: AssignParticipantInput }) =>
      post<null>(`/pm/issues/${issueId}/workflow/assign`, data),
    onSuccess: (_, { issueId, data }) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.participants(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.participants(issueId, data.stage) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history(issueId) })
    },
  })
}

/**
 * 提交审核
 */
export function useSubmitForReview() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (issueId: string) => post<null>(`/pm/issues/${issueId}/workflow/submit`),
    onSuccess: (_, issueId) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.config(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history(issueId) })
    },
  })
}

/**
 * 审批通过
 */
export function useApproveWorkflow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (issueId: string) => post<null>(`/pm/issues/${issueId}/workflow/approve`),
    onSuccess: (_, issueId) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.config(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.all })
    },
  })
}

/**
 * 审批拒绝/回退
 */
export function useRejectWorkflow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, data }: { issueId: string; data: RejectWorkflowInput }) =>
      post<null>(`/pm/issues/${issueId}/workflow/reject`, data),
    onSuccess: (_, { issueId }) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.config(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.all })
    },
  })
}

/**
 * 获取阶段参与人员
 */
export function useStageParticipants(issueId: string, stage?: WorkflowStage) {
  return useQuery({
    queryKey: workflowKeys.participants(issueId, stage),
    queryFn: () =>
      get<StageParticipant[]>(`/pm/issues/${issueId}/workflow/participants`, stage ? { stage } : {}),
    enabled: !!issueId,
  })
}

/**
 * 获取工作流历史
 */
export function useWorkflowHistory(issueId: string) {
  return useQuery({
    queryKey: workflowKeys.history(issueId),
    queryFn: () => get<WorkflowHistoryRecord[]>(`/pm/issues/${issueId}/workflow/history`),
    enabled: !!issueId,
  })
}

/**
 * 获取我的工作流任务
 */
export function useMyWorkflowTasks(params: MyWorkflowTasksParams = {}) {
  return useQuery({
    queryKey: workflowKeys.myTasks(params),
    queryFn: () =>
      getPaginated<MyWorkflowTask>('/pm/workflow/my-tasks', params as Record<string, unknown>),
  })
}

// ==================== Track Display Names ====================

export const TRACK_DISPLAY_NAMES: Record<TrackType, string> = {
  frontend: '前端',
  backend: '后端',
}

export const TRACK_STATUS_DISPLAY_NAMES: Record<TrackStatus, string> = {
  pending: '等待开始',
  in_progress: '开发中',
  in_review: '审查中',
  approved: '已通过',
  skipped: '已跳过',
}

export function getTrackDisplayName(track: TrackType): string {
  return TRACK_DISPLAY_NAMES[track] ?? track
}

export function getTrackStatusDisplayName(status: TrackStatus): string {
  return TRACK_STATUS_DISPLAY_NAMES[status] ?? status
}

// ==================== Track Hooks ====================

/**
 * 开始轨道开发
 */
export function useStartTrack() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, track, data }: { issueId: string; track: TrackType; data: StartTrackInput }) =>
      put<null>(`/pm/issues/${issueId}/workflow/track/${track}/start`, data),
    onSuccess: (_, { issueId }) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.config(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.all })
    },
  })
}

/**
 * 提交轨道审查
 */
export function useSubmitTrackForReview() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, track }: { issueId: string; track: TrackType }) =>
      put<null>(`/pm/issues/${issueId}/workflow/track/${track}/submit`),
    onSuccess: (_, { issueId }) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.config(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.all })
    },
  })
}

/**
 * 审批轨道通过
 */
export function useApproveTrack() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, track }: { issueId: string; track: TrackType }) =>
      put<{ converged: boolean }>(`/pm/issues/${issueId}/workflow/track/${track}/approve`),
    onSuccess: (_, { issueId }) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.config(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.all })
    },
  })
}

/**
 * 更新轨道负责人
 * Requirements: 6.x
 */
export function useUpdateTrackAssignee() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      issueId,
      track,
      assigneeId,
    }: {
      issueId: string
      track: TrackType
      assigneeId: string
    }) =>
      put<null>(`/pm/issues/${issueId}/workflow/tracks/${track}/assignee`, { assigneeId }),
    onSuccess: (_, { issueId }) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.config(issueId) })
    },
  })
}

/**
 * 拒绝轨道审查
 */
export function useRejectTrack() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, track, data }: { issueId: string; track: TrackType; data: RejectTrackInput }) =>
      put<null>(`/pm/issues/${issueId}/workflow/track/${track}/reject`, data),
    onSuccess: (_, { issueId }) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.config(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.all })
    },
  })
}

/**
 * 从测试阶段回退
 */
export function useRollbackFromTesting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, data }: { issueId: string; data: RollbackFromTestingInput }) =>
      post<null>(`/pm/issues/${issueId}/workflow/rollback`, data),
    onSuccess: (_, { issueId }) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.config(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.all })
    },
  })
}

/**
 * 获取我的轨道任务
 */
export function useMyTrackTasks(params: MyTrackTasksParams = {}) {
  return useQuery({
    queryKey: workflowKeys.myTrackTasks(params),
    queryFn: () =>
      getPaginated<MyTrackTask>('/pm/workflow/my-track-tasks', params as Record<string, unknown>),
  })
}

// ==================== Stage Reviewer Hooks ====================

/**
 * 设置单个阶段审核人
 */
export function useSetStageReviewer() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, stage, reviewerId }: { issueId: string; stage: WorkflowStage; reviewerId: string | null }) =>
      put<null>(`/pm/issues/${issueId}/workflow/stages/${stage}/reviewer`, { reviewerId }),
    onSuccess: (_, { issueId }) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.config(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history(issueId) })
    },
  })
}

/**
 * 批量设置阶段审核人
 */
export function useSetStageReviewers() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, reviewers }: { issueId: string; reviewers: SetStageReviewerInput[] }) =>
      put<null>(`/pm/issues/${issueId}/workflow/reviewers`, { reviewers }),
    onSuccess: (_, { issueId }) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.config(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history(issueId) })
    },
  })
}

/**
 * 添加阶段参与人员
 */
export function useAddStageParticipants() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, stage, employeeIds }: { issueId: string; stage: WorkflowStage; employeeIds: string[] }) =>
      post<null>(`/pm/issues/${issueId}/workflow/stages/${stage}/participants`, { employeeIds }),
    onSuccess: (_, { issueId, stage }) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.participants(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.participants(issueId, stage) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.history(issueId) })
    },
  })
}

/**
 * 移除阶段参与人员
 */
export function useRemoveStageParticipant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ issueId, stage, employeeId }: { issueId: string; stage: WorkflowStage; employeeId: string }) =>
      del<null>(`/pm/issues/${issueId}/workflow/stages/${stage}/participants/${employeeId}`),
    onSuccess: (_, { issueId, stage }) => {
      void queryClient.invalidateQueries({ queryKey: workflowKeys.participants(issueId) })
      void queryClient.invalidateQueries({ queryKey: workflowKeys.participants(issueId, stage) })
    },
  })
}
